import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFetchBaseQuery } from "../apiFetchBaseQuery";
import {
	GetRtbNetworkByIdResponse,
	GetRtbNetworkListResponse,
	GetRtbNetworkSetupResponse,
	RtbNetworkSetupFormDataPayload,
	RtbNetworkSetupResponse,
} from "@/types/oRtbNetwork";
import { PaginationParams } from "@/types/Pagination";

export const rtbNetworkApiSlice = createApi({
	reducerPath: "oRtbNetwork",
	baseQuery: apiFetchBaseQuery,
	tagTypes: ["networkList", "fetchNetwork", "networkSetup"],
	endpoints: builder => ({
		getRtbNetworkList: builder.query<GetRtbNetworkListResponse, PaginationParams>({
			query: ({ pageNo, pageSize }) => ({
				url: `rtb/networks`,
				params: {
					page_no: pageNo,
					limit: pageSize,
				},
			}),
			providesTags: ["networkList"],
		}),
		getRtbNetworkById: builder.query<GetRtbNetworkByIdResponse, { networkId: number }>({
			query: ({ networkId }) => ({
				url: `rtb/networks/${networkId}`,
			}),
			providesTags: ["fetchNetwork"],
		}),
		getRtbNetworkSetupConfig: builder.query<GetRtbNetworkSetupResponse, { networkId: number }>({
			query: ({ networkId }) => ({
				url: "rtb/networks/setup-config",
				params: {
					network_id: networkId ?? "",
				},
			}),
			providesTags: ["networkSetup"],
		}),
		upsertRtbNetwork: builder.mutation<
			RtbNetworkSetupResponse,
			{ formData: RtbNetworkSetupFormDataPayload; networkId?: number }
		>({
			query: ({ formData, networkId = 0 }) => {
				const urlEncodedPayload = new URLSearchParams();

				Object.entries(formData).forEach(([key, value]) => {
					urlEncodedPayload.append(key, String(value));
				});

				let url = "rtb/networks";
				let method = "POST";
				if (networkId > 0) {
					url = `rtb/networks/${networkId}`;
					method = "PUT";
				}

				return {
					url,
					method,
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: urlEncodedPayload.toString(),
				};
			},
			invalidatesTags: ["networkList"],
		}),
		deleteRtbNetwork: builder.mutation<null, { networkId: number }>({
			query: ({ networkId }) => ({
				url: `rtb/networks/${networkId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["networkList"],
		}),
	}),
});

export const {
	useGetRtbNetworkListQuery,
	useLazyGetRtbNetworkByIdQuery,
	useLazyGetRtbNetworkSetupConfigQuery,
	useUpsertRtbNetworkMutation,
	useDeleteRtbNetworkMutation,
} = rtbNetworkApiSlice;
